/**
 * Client-side decode of the L3 access token's claims (Account Control Plane P5).
 *
 * This is a DECODE, never a verify: the L3 server verifies the ES256 signature
 * against the CP JWKS on every request. The console decodes only to read the
 * authorization scope — a superadmin's L3 token carries `scope: superadmin`,
 * which is what the console gates admin access on.
 */
export const SCOPE_SUPERADMIN = 'superadmin'

export interface L3TokenClaims {
  /** `sub` — Human Identity id (CUID). */
  readonly subject: string
  /** `org` — derived Organization id; null for identity-only tokens. */
  readonly organizationId: string | null
  /** `role` — signed org-context role string. DISPLAY/AUDIT ONLY. */
  readonly role: string | null
  /** `perms` — signed authorization bundle (advisory for UI; L3 enforces). */
  readonly permissions: string[]
  /** `scope` — token scopes (space-delimited on the wire), e.g. superadmin. */
  readonly scopes: string[]
  /** `exp` — expiry, epoch milliseconds. */
  readonly expiresAtMs: number
}

interface WireClaims {
  sub?: unknown
  org?: unknown
  role?: unknown
  perms?: unknown
  scope?: unknown
  exp?: unknown
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/')
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4))
  return atob(padded + pad)
}

/**
 * Decode the claims of a compact JWS. Returns null on any malformed input —
 * callers treat a null decode as "no CP session".
 */
export function decodeL3TokenClaims(jwt: string): L3TokenClaims | null {
  const parts = jwt.split('.')
  if (parts.length !== 3) return null
  let wire: WireClaims
  try {
    wire = JSON.parse(base64UrlDecode(parts[1])) as WireClaims
  } catch {
    return null
  }
  if (typeof wire.sub !== 'string' || wire.sub.length === 0) return null
  return {
    subject: wire.sub,
    organizationId: typeof wire.org === 'string' && wire.org.length > 0 ? wire.org : null,
    role: typeof wire.role === 'string' && wire.role.length > 0 ? wire.role : null,
    permissions: Array.isArray(wire.perms)
      ? wire.perms.filter((p): p is string => typeof p === 'string')
      : [],
    scopes:
      typeof wire.scope === 'string' && wire.scope.length > 0
        ? wire.scope.split(' ').filter(Boolean)
        : [],
    expiresAtMs: typeof wire.exp === 'number' ? wire.exp * 1000 : 0,
  }
}
